/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { get } from 'lodash';

import { IGeneralNavigationLinksData, IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { NavIcon } from './components/nav-icon.component';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type ToggleFn = () => void;

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>> {
    private readonly menuNode: React.RefObject<HTMLDivElement>;
    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {
        super(props);
        this.menuNode = React.createRef();
    }

    public componentDidMount(): void {
        if (this.props.config.useStickyHeader) {
            // Set header update methods to trigger on scroll
            if (MsDyn365.isBrowser) {
                window.addEventListener('scroll', () => {
                    this._configureStickyHeader();
                });
            }
            this._configureStickyHeader();
        }
        this.handleLoginRedirect();
    }

    private handleLoginRedirect() {
        const {
            context: {
                actionContext,
                request: {
                    user: { isAuthenticated, isOBORequest }
                }
            }
        } = this.props;
        const isHomePage = actionContext.requestContext.url.requestUrl.pathname === '/';
        const customer = this.props.data.accountInformation?.result;
        if (MsDyn365.isBrowser && typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            if (url.searchParams.has('catalogid')) {
                url.searchParams.delete('catalogid');

                window.location.href = url.toString();
            }
        }

        if (isAuthenticated) {
            if (isHomePage) {
                if (isOBORequest && !customer) {
                    window.location.href = getUrlSync('businesses', this.props.context.actionContext) || '/businesses';
                } else {
                    window.location.href = getUrlSync('account', this.props.context.actionContext) || '/myaccount';
                }
            } else {
                const accountUrl = getUrlSync('account', this.props.context.actionContext);
                const logoAnchorTag = document.querySelector<HTMLAnchorElement>('.ms-header__logo>a');
                if (logoAnchorTag && accountUrl) {
                    logoAnchorTag.href = accountUrl;
                }
            }
        }
    }

    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer,
            Divider,
            MobileMenuContainer,
            MobileMenuBodyContainer,
            MobileMenuLinksContainer,
            config: {
                showQuickOrderLink,
                showPhoneNumber,
                phoneNumber,
                generalNavigationLinks,
                shouldHideMenuNavigation,
                shouldDisplayGeneralNavLinks,
                headerMenuText
            },
            mobileMenuCollapsed
        } = this.props;
        const originalNavIconElement = this.props.navIcon as React.ReactElement | null;
        const fallbackClick = () => {
            const container = this.menuNode?.current as HTMLElement | null;
            const btn = container?.querySelector('.ms-header__nav-icon.msc-btn') as HTMLButtonElement | null;
            if (btn) {
                btn.click();
            }
        };

        const originalToggle: ToggleFn =
            (originalNavIconElement && (originalNavIconElement.props?.toggleNavBar as ToggleFn)) ||
            (() => {
                if (typeof (this as any)._toggleNavbar === 'function') {
                    (this as any)._toggleNavbar();
                    return;
                }
                fallbackClick();
            });

        const hideMenuNavigation = shouldHideMenuNavigation ?? false;
        const headingMenuText = headerMenuText ? headerMenuText : 'Menu';

        const quickOrderUrl = getUrlSync('quickorder', this.props.context.actionContext);
        if (shouldDisplayGeneralNavLinks && generalNavigationLinks && generalNavigationLinks.length > 0) {
            return (
                <div key={'general navigation'}>
                    <Module {...HeaderTag} key={'general navigation'}>
                        <Node {...HeaderContainer}>
                            <Node {...HeaderTopBarContainer}>
                                <div className={'ms-header__desktop-logo'}>
                                    {this.props.logo}
                                    {showPhoneNumber == 'yes' && phoneNumber ? this._renderPhoneNumber(this.props) : null}
                                </div>
                                <div className='ms-header__general-nav'>
                                    <div className='mobile-navicon-container'>
                                        {/* {this.props.navIcon} <span className='mobile-navicon-text'>{headingMenuText}</span> */}
                                        <NavIcon
                                            headerMenuText={headingMenuText}
                                            isExpanded={!mobileMenuCollapsed}
                                            toggleNavBar={originalToggle}
                                        />
                                    </div>
                                    {!hideMenuNavigation && (
                                        <div className='desktop-navicon-container' ref={this.menuNode}>
                                            {this._renderReactFragment(this.props.menuBar)}
                                        </div>
                                    )}
                                    <div className='ms-header__general-links-group'>
                                        {generalNavigationLinks?.map((link, index) => {
                                            return this._renderGeneralPageLinks({
                                                link,
                                                isFirstChild: index === 0 && !hideMenuNavigation && this.props.menuBar.length > 0
                                            });
                                        })}
                                    </div>
                                    {this._renderMobileLogo()}
                                    {this._renderAccountBlock(this.props)}
                                </div>
                            </Node>
                            <Node {...MobileMenuContainer}>
                                <Node {...MobileMenuBodyContainer}>
                                    {this.props.MobileMenuHeader}
                                    {this._renderReactFragment(this.props.menuBar)}
                                    <Node {...MobileMenuLinksContainer}>
                                        {this.props.accountLinks ? this.props.accountLinks.map(link => link) : false}
                                        {this.props.siteOptions}
                                        {this.props.wishListIconMobile}
                                        {this.props.signInLink}
                                        {this.props.signOutLink}
                                    </Node>
                                </Node>
                            </Node>
                        </Node>
                    </Module>
                </div>
            );
        }
        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        {/* <div className='mobile-navicon-container'>{this.props.navIcon}</div> */}
                        <div className={'ms-header__desktop-logo'}>
                            {this.props.logo}
                            {showPhoneNumber == 'yes' && phoneNumber ? this._renderPhoneNumber(this.props) : null}
                        </div>
                        <div className='ms-header__desktop-nav-items'>
                            <div className='mobile-navicon-container'>
                                {/* {this.props.navIcon} <span className='mobile-navicon-text'>{headingMenuText}</span> */}
                                <NavIcon headerMenuText={headingMenuText} isExpanded={!mobileMenuCollapsed} toggleNavBar={originalToggle} />
                            </div>
                            <div className='desktop-navicon-container' ref={this.menuNode}>
                                {this._renderReactFragment(this.props.menuBar)}
                            </div>
                            {this._renderMobileLogo()}
                            {this._renderReactFragment(this.props.search)}
                            {this.props.preferredStore}
                            {this._renderAccountBlock(this.props)}
                            <Node {...Divider} />
                            {showQuickOrderLink ? this._renderQuickViewIcon(quickOrderUrl || '#') : null}
                            {this.props.cartIcon}
                            {this._renderReactFragment(this.props.siteOptions)}
                        </div>
                    </Node>
                    <Node {...MobileMenuContainer}>
                        <Node {...MobileMenuBodyContainer}>
                            {this.props.MobileMenuHeader}
                            {this._renderReactFragment(this.props.menuBar)}
                            <Node {...MobileMenuLinksContainer}>
                                {this.props.accountLinks ? this.props.accountLinks.map(link => link) : false}
                                {this.props.siteOptions}
                                {this.props.wishListIconMobile}
                                {this.props.signInLink}
                                {this.props.signOutLink}
                            </Node>
                        </Node>
                    </Node>
                </Node>
            </Module>
        );
    }

    /**
     * Function to render phone number beside the logo.
     * @param props - IHeaderProps.
     * @returns Returns JSX.Element | null.
     */
    private _renderPhoneNumber(props: IHeaderProps<{}>): JSX.Element | null {
        const {
            config: { phoneNumber }
        } = props;
        const telephoneLink: string = `tel:+1-${phoneNumber}`;
        return (
            <div className='ms-header__phone-number'>
                <a href={telephoneLink} className='ms-header__phone-number-link'>
                    {phoneNumber ? phoneNumber : ''}
                </a>
            </div>
        );
    }

    private _renderQuickViewIcon = (quickOrderLink: string) => (
        <div className={'ms-header__quick-order'}>
            <a href={quickOrderLink} className={'ms-header__quick-order-button'}>
                <span className={'ms-header__quick-order-button-text'}>Quick Order</span>
            </a>
        </div>
    );

    private _renderGeneralPageLinks = ({ link, isFirstChild }: { link: IGeneralNavigationLinksData; isFirstChild: boolean }) => {
        return (
            <div className={classnames('ms-header__general-page-link', isFirstChild && 'is-first')}>
                <a href={link.linkUrl.destinationUrl} aria-label={link.ariaLabel} className={'ms-header__general-page-link-button'}>
                    <span className={'ms-header__general-page-link-button-text'}>{link.linkText}</span>
                </a>
            </div>
        );
    };

    /**
     * Function to update header.
     */
    private readonly _configureStickyHeader = (): void => {
        // Get heights of cookie and promotion banners
        const defaultValue = 0;
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : defaultValue;

        // Triggers opacity change of header
        const headerElement = document.querySelector('header .default-container');
        if (headerElement) {
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > defaultValue) {
                headerElement.classList.add('lock-opaque');
                headerElement.classList.add('fixed');
            } else {
                headerElement.classList.remove('lock-opaque');
                headerElement.classList.remove('fixed');
            }
        }

        // Update sticky header position and opacity
        const stickyHeader: HTMLElement | null = document.querySelector('.desktop-navicon-container');
        const headerLogo: HTMLElement | null = document.querySelector('.ms-header__logo');

        if (stickyHeader && headerLogo) {
            // Fix center sticky header
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else {
                stickyHeader.classList.remove('fixed');
            }
        }
    };

    private _renderMobileLogo(): JSX.Element | null {
        const { config, context, typeName } = this.props;
        return (
            <Logo
                {...{
                    link: config.logoLink,
                    image: config.mobileLogoImage ?? {},
                    className: 'ms-header__mobile-logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName
                }}
            />
        );
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderAccountBlock = (props: IHeaderViewProps): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            AccountInfoDropdownParentContainer,
            AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signOutLink,
            signInLink,
            accountLinks,
            context: {
                request: {
                    user: { isOBORequest }
                }
            }
        } = props;
        if (AccountInfoDropdownParentContainer) {
            const changeUserSlug = 'Change User'; // Change user page
            let accountLinksFiltered: React.ReactNode[] = [];
            if (isOBORequest) {
                // If we're mocking a user, show the change user button, if we are not, remove it before render.
                accountLinksFiltered = accountLinks || [];
            } else if (accountLinks) {
                accountLinks.forEach(node => {
                    const linkUrl = get(node, 'props.link.linkText');
                    if (linkUrl && (linkUrl as string) !== changeUserSlug) {
                        accountLinksFiltered.push(node);
                    }
                });
            }
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>
                            {accountLinksFiltered.map((link: React.ReactNode) => link)}
                            {signOutLink}
                        </Node>
                    </Node>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {signInLink}
                    </Node>
                );
            }
        }
        return null;
    };

    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items)
                    ? items.map((slot: React.ReactNode) => {
                          return <>{slot}</>;
                      })
                    : null}
            </>
        );
    };
}

export default HeaderView;
